import { post, request } from '../api';
import type { RunStatus } from './runs';

export const TASK_STATUSES = ['backlog', 'planned', 'in_progress', 'review', 'waiting_human', 'done', 'rejected', 'deferred'] as const;
export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export type TaskPriority = (typeof TASK_PRIORITIES)[number];
export type TaskSort = 'updatedAt' | 'title' | 'dueAt' | 'priority' | 'number';
export type RelationKind = 'blocks' | 'relates' | 'client' | 'asset';

export interface ActorRef { id: string; displayName: string; type: 'human' | 'agent' }
export interface TaskRef { id: string; key: string; title: string; status: TaskStatus }

export interface TaskRow {
  id: string;
  key: string;
  number: number;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  dueAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  businessId: string;
  projectId: string | null;
  assigneeId: string | null;
  business: { id: string; name: string; taskPrefix: string };
  project: { id: string; name: string } | null;
  assignee: ActorRef | null;
}

/** `metadata.source === 'agent'` marks a comment the office connector wrote for a run (ADR-0004 decision 4). */
export interface TaskComment { id: string; parentId: string | null; body: string; createdAt: string; updatedAt: string; author: ActorRef; metadata?: Record<string, unknown> | null }
export interface TaskActivity { id: string; action: string; before: Record<string, unknown> | null; after: Record<string, unknown> | null; createdAt: string; actor: ActorRef | null }
export interface TaskRelationOut { id: string; kind: RelationKind; direction: 'out'; targetTaskId: string | null; targetAssetId: string | null; targetObservationId: string | null; targetTask: TaskRef | null }
export interface TaskRelationIn { id: string; kind: RelationKind; direction: 'in'; task: TaskRef | null }

export interface TaskBusiness { id: string; name: string; slug: string; status: string; taskPrefix: string; timezone: string }
export interface TaskMember { actorId: string; displayName: string; type: 'human' | 'agent' }

export interface TaskDetail extends TaskRow {
  /** Actors referenced from the history (assignees), so names resolve even when they are no longer members. */
  actors: ActorRef[];
  process: { id: string; name: string } | null;
  createdBy: ActorRef | null;
  comments: TaskComment[];
  activity: TaskActivity[];
  relations: TaskRelationOut[];
  relatedBy: TaskRelationIn[];
  plans: Array<{ id: string; version: number; status: string }>;
  /** The latest agent runs of this task (newest first); the Runs screen has the full list. */
  executions: TaskExecution[];
}

export interface TaskExecution {
  id: string;
  status: RunStatus;
  agentInstanceId: string;
  agentName: string;
  model: string;
  cost: string;
  currency: string;
  attempt: number;
  durationSec: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export interface TaskListParams {
  businessId?: string;
  status?: TaskStatus[];
  assigneeId?: string;
  projectId?: string;
  q?: string;
  sort?: TaskSort;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}
export interface TaskListResult { rows: TaskRow[]; total: number; page: number; pageSize: number }
export type TaskCounts = Record<TaskStatus | 'active', number>;
export interface ProjectRef { id: string; name: string; status: string }

export interface CreateTaskInput { businessId: string; projectId?: string | null; title: string; description?: string | null; priority?: TaskPriority; assigneeId?: string | null; dueAt?: string | null }
export interface UpdateTaskInput { version: number; title?: string; description?: string | null; projectId?: string | null; priority?: TaskPriority; assigneeId?: string | null; dueAt?: string | null }

function qs(params: Record<string, string | number | string[] | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => sp.append(k, x));
    else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const tasksApi = {
  list: (p: TaskListParams) => request<TaskListResult>(`/tasks${qs({ ...p })}`),
  counts: (businessId?: string) => request<TaskCounts>(`/tasks/counts${qs({ businessId })}`),
  projects: (businessId: string) => request<ProjectRef[]>(`/tasks/projects${qs({ businessId })}`),
  businesses: () => request<TaskBusiness[]>('/tasks/businesses'),
  members: (businessId?: string) => request<TaskMember[]>(`/tasks/members${qs({ businessId })}`),
  get: (id: string) => request<TaskDetail>(`/tasks/${encodeURIComponent(id)}`),
  create: (input: CreateTaskInput) => post<TaskRow>('/tasks', input),
  update: (id: string, input: UpdateTaskInput) => request<TaskRow>(`/tasks/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  setStatus: (id: string, version: number, status: TaskStatus) => post<TaskRow>(`/tasks/${encodeURIComponent(id)}/status`, { version, status }),
  addComment: (id: string, body: string, parentId?: string | null) => post<TaskComment>(`/tasks/${encodeURIComponent(id)}/comments`, { body, parentId: parentId ?? null }),
  editComment: (id: string, body: string) => request<TaskComment>(`/comments/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ body }) }),
  startRun: (id: string, agentInstanceId: string) => post<{ executionId: string; taskKey: string }>(`/tasks/${encodeURIComponent(id)}/run`, { agentInstanceId }),
  addRelation: (id: string, input: { kind: RelationKind; targetTaskId?: string; targetAssetId?: string; targetObservationId?: string }) => post<TaskRelationOut>(`/tasks/${encodeURIComponent(id)}/relations`, input),
  removeRelation: (id: string) => request<{ ok: true }>(`/relations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};
